import type { GameMode } from '../types/gameMode.types';
import { STUDY_GUIDE_THEORY_MODES } from '../types/payloads/evaluacion.types';
import { normalizeLatexString } from '../../activities/guide_editorial/guide_editorial.logic';
import {
  enforceHumanitiesGuideBlocks,
  isDuplicateBody,
  normalizeTextForCompare,
} from '../print/humanitiesGuideEnforcer';
import { SPIRAL_BLOCK_THRESHOLD, enforceGuideSpiralStructure } from '../print/guideSpiralEnforcer';
import { enforceGenreShowDontTell } from '../print/genreStimulusEnforcer';

export interface StudyGuideBlockLike {
  mode: string;
  payload?: Record<string, unknown>;
  label?: string;
}

const PRACTICE_MODES = new Set<GameMode>([
  'arena',
  'completar',
  'vincula',
  'agrupa',
  'order',
  'desarrollo',
  'data_chart',
  'source_analysis',
]);

const CONTENT_MODES = new Set<string>([
  'info_block',
  'destacado',
  'figura',
  'canvas_breakdown',
  'structural_breakdown',
]);

function isContentMode(mode: string): boolean {
  return CONTENT_MODES.has(mode);
}

function headerNeedsContent(blocks: StudyGuideBlockLike[], index: number): boolean {
  const block = blocks[index];
  if (!block || block.mode !== 'subtitulo') return false;
  const next = blocks[index + 1];
  if (!next) return true;
  if (next.mode === 'subtitulo') return true;
  if (next.mode === 'formula_destacada' || next.mode === 'glosario_tabla') return true;
  return false;
}

function expandSubtitleBodies<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  const result: T[] = [];

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!;
    result.push(block);
    if (block.mode !== 'subtitulo') continue;

    const payload = (block.payload ?? {}) as Record<string, unknown>;
    const subtitle = String(payload.subtitle || '').trim();
    if (!subtitle) continue;

    const next = blocks[i + 1];
    if (next && isContentMode(next.mode)) continue;

    result.push({
      mode: 'info_block',
      label: String(payload.title || block.label || 'Sección'),
      payload: {
        title: String(payload.title || ''),
        bodyText: subtitle,
      },
    } as T);
  }

  return result;
}

function splitInfoBlockForOrphanHeaders<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  const orphanIndices = blocks
    .map((_, i) => (headerNeedsContent(blocks, i) ? i : -1))
    .filter((i) => i >= 0);

  if (!orphanIndices.length) return blocks;

  const infoIdx = blocks.findIndex((b) => b.mode === 'info_block');
  if (infoIdx < 0) return blocks;

  const info = blocks[infoIdx]!;
  const bodyText = String((info.payload as Record<string, unknown> | undefined)?.bodyText || '').trim();
  const paragraphs = bodyText.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length < 2) return blocks;

  const needCount = orphanIndices.length;
  const introCount = Math.max(1, paragraphs.length - needCount);
  const introParagraphs = paragraphs.slice(0, introCount);
  const sectionParagraphs = paragraphs.slice(introCount);

  const next = [...blocks];
  next[infoIdx] = {
    ...info,
    payload: {
      ...(info.payload as Record<string, unknown>),
      bodyText: introParagraphs.join('\n\n'),
    },
  } as T;

  const usedBodies = new Set<string>([
    normalizeTextForCompare(introParagraphs.join('\n\n')),
  ]);

  let offset = 0;
  for (let o = 0; o < orphanIndices.length; o++) {
    const text = String(sectionParagraphs[o] || '').trim();
    if (!text || isDuplicateBody(text, usedBodies)) continue;

    const idx = orphanIndices[o]! + offset;
    const header = next[idx]!;
    usedBodies.add(normalizeTextForCompare(text));

    const insert = {
      mode: 'info_block',
      label: String((header.payload as Record<string, unknown> | undefined)?.title || header.label || 'Sección'),
      payload: {
        title: String((header.payload as Record<string, unknown> | undefined)?.title || ''),
        bodyText: text,
      },
    } as T;

    next.splice(idx + 1, 0, insert);
    offset += 1;
  }

  return next;
}

const INTRO_HOOK_PATTERNS: RegExp[] = [
  /¿Te has preguntado[^.?!]+[.?!]\s*/gi,
  /En esta guía (?:lo )?descubrirás[^.?!]*[.?!]\s*/gi,
];

function stripIntroHookSentences(text: string): string {
  let out = String(text || '');
  for (const pattern of INTRO_HOOK_PATTERNS) {
    out = out.replace(pattern, '');
  }
  return out.replace(/\s{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

function cleanRecycledText(bodyText: string, introNorm: string, introParagraphs: string[]): string {
  const paragraphs = bodyText.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const filtered = paragraphs
    .map((paragraph) => stripIntroHookSentences(paragraph))
    .filter((paragraph) => {
      const norm = normalizeTextForCompare(paragraph);
      if (!norm) return false;
      if (norm === introNorm) return false;
      if (introNorm.includes(norm) || norm.includes(introNorm)) return false;
      return !introParagraphs.some(
        (introPara) =>
          introPara === norm ||
          (introPara.length > 50 && (introPara.includes(norm) || norm.includes(introPara))),
      );
    });

  return filtered.join('\n\n').trim();
}

export function removeRecycledIntroductions<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  const introIdx = blocks.findIndex((b) => b.mode === 'info_block');
  if (introIdx < 0) return blocks;

  const introBody = String((blocks[introIdx]!.payload as Record<string, unknown> | undefined)?.bodyText || '').trim();
  if (introBody.length < 40) return blocks;

  const introNorm = normalizeTextForCompare(introBody);
  const introParagraphs = introBody
    .split(/\n\n+/)
    .map((p) => normalizeTextForCompare(stripIntroHookSentences(p)))
    .filter((p) => p.length >= 30);

  const next = blocks
    .map((block, index) => {
      if (index === introIdx) return block;

      const payload = { ...(block.payload as Record<string, unknown>) };

      if (block.mode === 'info_block') {
        const bodyText = String(payload.bodyText || '').trim();
        if (!bodyText) return block;
        const cleaned = cleanRecycledText(bodyText, introNorm, introParagraphs);
        if (cleaned === bodyText) return block;
        return { ...block, payload: { ...payload, bodyText: cleaned } };
      }

      if (block.mode === 'destacado') {
        const body = String(payload.body || '').trim();
        if (!body) return block;
        const cleaned = cleanRecycledText(body, introNorm, introParagraphs);
        if (cleaned === body) return block;
        return { ...block, payload: { ...payload, body: cleaned } };
      }

      return block;
    })
    .filter((block) => {
      if (block.mode === 'info_block') {
        return String((block.payload as Record<string, unknown> | undefined)?.bodyText || '').trim().length > 0;
      }
      if (block.mode === 'destacado') {
        return String((block.payload as Record<string, unknown> | undefined)?.body || '').trim().length > 0;
      }
      return true;
    });

  return next;
}

function stripLatexFromInfoBlocks<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  const hasFormula = blocks.some((b) => b.mode === 'formula_destacada');
  if (!hasFormula) return blocks;

  return blocks.map((block) => {
    if (block.mode !== 'info_block') return block;
    const payload = { ...(block.payload as Record<string, unknown>) };
    const body = String(payload.bodyText || '');
    const cleaned = body
      .split('\n')
      .filter((line) => {
        const t = line.trim();
        if (!t) return true;
        return !/\\frac|\\Delta|\\FRAC|\\DELTA|\\times|\\TIMES/i.test(t);
      })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { ...block, payload: { ...payload, bodyText: cleaned } };
  });
}

function normalizeFormulaPayloads<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  return blocks.map((block) => {
    if (block.mode !== 'formula_destacada') return block;
    const payload = { ...(block.payload as Record<string, unknown>) };
    if (payload.latex) payload.latex = normalizeLatexString(String(payload.latex));
    if (payload.formula) payload.formula = normalizeLatexString(String(payload.formula));
    return { ...block, payload };
  });
}

/** Repara atlas mal generados: subtítulos huérfanos, LaTeX duplicado y fórmulas en lectura. */
export function repairStudyGuideBlocks<T extends StudyGuideBlockLike>(
  blocks: T[],
  subjectTag?: string,
): T[] {
  if (blocks.length < 2) {
    return enforceGenreShowDontTell(
      enforceHumanitiesGuideBlocks(normalizeFormulaPayloads(blocks), subjectTag),
      subjectTag,
    );
  }

  let repaired = expandSubtitleBodies(blocks);
  repaired = splitInfoBlockForOrphanHeaders(repaired);
  repaired = removeRecycledIntroductions(repaired);
  repaired = stripLatexFromInfoBlocks(repaired);
  repaired = normalizeFormulaPayloads(repaired);
  repaired = enforceHumanitiesGuideBlocks(repaired, subjectTag);
  repaired = enforceGenreShowDontTell(repaired, subjectTag);
  return repaired;
}

/** Detecta atlas teórico aunque falte la bandera isStudyGuide (exámenes guardados antes). */
export function inferStudyGuide(exam: {
  isStudyGuide?: boolean;
  resourceType?: string;
  deliveryProfile?: string;
  blocks?: StudyGuideBlockLike[];
}): boolean {
  if (exam.isStudyGuide === true) return true;
  if (exam.deliveryProfile === 'learning_guide') return false;
  if (exam.resourceType !== 'guide') return false;
  const blocks = exam.blocks ?? [];
  if (!blocks.length) return false;
  const theory = new Set<string>(STUDY_GUIDE_THEORY_MODES);
  const hasTheory = blocks.some((b) => theory.has(b.mode));
  const hasPractice = blocks.some((b) => PRACTICE_MODES.has(b.mode as GameMode));
  return hasTheory && !hasPractice;
}

/**
 * Reordena bloques de atlas: título → contenido → título → contenido…
 * Fórmulas tras la primera sección; glosario al final.
 */
export function reorderStudyGuideBlocks<T extends StudyGuideBlockLike>(blocks: T[]): T[] {
  if (blocks.length < 2) return blocks;

  const headers: T[] = [];
  const formulas: T[] = [];
  const glossaries: T[] = [];
  const contents: T[] = [];

  for (const block of blocks) {
    switch (block.mode) {
      case 'subtitulo':
        headers.push(block);
        break;
      case 'formula_destacada':
        formulas.push(block);
        break;
      case 'glosario_tabla':
        glossaries.push(block);
        break;
      default:
        contents.push(block);
        break;
    }
  }

  if (!headers.length) {
    return [...contents, ...formulas, ...glossaries];
  }

  const result: T[] = [];
  let contentIdx = 0;

  const firstContentOriginal = blocks.findIndex(
    (b) => b.mode !== 'subtitulo' && b.mode !== 'glosario_tabla' && b.mode !== 'formula_destacada',
  );
  const firstHeaderOriginal = blocks.findIndex((b) => b.mode === 'subtitulo');
  const introBeforeHeader =
    firstContentOriginal >= 0 &&
    (firstHeaderOriginal < 0 || firstContentOriginal < firstHeaderOriginal);

  if (introBeforeHeader && contentIdx < contents.length) {
    result.push(contents[contentIdx++]!);
  }

  for (let i = 0; i < headers.length; i++) {
    result.push(headers[i]!);
    if (contentIdx < contents.length) {
      result.push(contents[contentIdx++]!);
    }
    if (i === 0 && formulas.length) {
      result.push(formulas.shift()!);
    }
  }

  while (contentIdx < contents.length) {
    result.push(contents[contentIdx++]!);
  }
  result.push(...formulas, ...glossaries);

  return result;
}

/** Repara y reordena bloques de atlas para impresión o persistencia. */
export function prepareStudyGuideBlocks<T extends StudyGuideBlockLike>(
  blocks: T[],
  subjectTag?: string,
): T[] {
  let repaired = repairStudyGuideBlocks(blocks, subjectTag);
  if (repaired.length >= SPIRAL_BLOCK_THRESHOLD) {
    return enforceGuideSpiralStructure(repaired);
  }
  return reorderStudyGuideBlocks(repaired);
}
